import compact from 'lodash/compact';
import isFunction from 'lodash/isFunction';
import {
  PaginationState,
} from '@tanstack/react-table';
import {
  DateTimeRangeDTO,
  EFaxFilterDTO,
  FaxContactDTO,
  FaxViewDTO,
  FaxViewDTOPaginatedResponseDTO,
  MultiMediaResponseDTO,
  PagePropertiesRequestDTO,
} from 'dtos';
import {
  serializeDateRange,
} from 'pages/Dashboard/transport/dateRange';
import {
  MediaOriginatorType,
} from 'pages/Dashboard/utils/constants';
import {
  DateRange,
} from 'types/dateRange';
import {
  sendRequest,
} from 'utils/httpClient';
import {
  normalizeContactsResponse,
  normalizeFaxesResponse,
} from 'pages/Dashboard/pages/Fax/util';
import {
  getApiEFaxGetFaxContacts,
  postApiEFaxGetFaxes,
} from 'endpoints';

export type ResendFaxParams = {
  outboundFaxId: number;
};

export const getMultiMediaByFaxId = (
  faxId: number,
): Promise<MultiMediaResponseDTO> => (
  sendRequest<MultiMediaResponseDTO>({
    url: '/api/Multimedia/getFaxMultimediaByFaxId',
  }, {
    method: 'GET',
    params: {
      faxId,
    },
  })
);

export type GetFaxesParams = {
  dateRange: DateTimeRangeDTO;
  pageRequest: PagePropertiesRequestDTO;
};

export const getFaxes = (
  data: GetFaxesParams,
  options?: { signal?: AbortSignal },
): Promise<FaxViewDTOPaginatedResponseDTO> => (
  sendRequest<FaxViewDTOPaginatedResponseDTO>({
    url: '/api/eFax/getFaxes',
    method: 'POST',
    data,
  }, options)
);

export type ContactsDataParams = {
  dateRange?: DateRange;
  pagination?: PaginationState;
  searchQuery?: string;
  setTotalSize?: (size: number) => void;
};

export type FaxesDataParams = {
  dateRange?: DateRange;
  pagination?: PaginationState;
  setTotalSize?: (size: number) => void;
  searchQuery?: string;
  filterQuery?: EFaxFilterDTO;
  requestMedia?: (ids: number[], originatorType?: MediaOriginatorType) => void;
};

export const getContactsData = async ({
  pagination,
  searchQuery,
  setTotalSize,
}: ContactsDataParams): Promise<any[]> => {
  const response = await getApiEFaxGetFaxContacts({
    searchText: searchQuery,
    page: pagination?.pageIndex ?? 0,
    pageSize: pagination?.pageSize ?? 25,
  });

  setTotalSize?.(response.pageProperties?.totalSize ?? 0);
  const normalized = normalizeContactsResponse(response);

  return normalized;
};

export const getFaxesData = async ({
  dateRange,
  pagination,
  setTotalSize,
  filterQuery,
  searchQuery,
  requestMedia,
}: FaxesDataParams, options?: { signal?: AbortSignal }): Promise<FaxViewDTO[]> => {
  const response = await postApiEFaxGetFaxes({
    dateRange: serializeDateRange(dateRange),
    pageRequest: {
      page: pagination?.pageIndex ?? 0,
      pageSize: pagination?.pageSize ?? 25,
    },
    filterQuery,
    searchText: searchQuery,
  }, options);

  setTotalSize?.(response.pageProperties?.totalSize ?? 0);
  const normalized = normalizeFaxesResponse(response);

  if (isFunction(requestMedia)) {
    const patientIds = compact(normalized.map((fax) => fax.patient?.patientId));
    if (patientIds.length > 0){
      requestMedia(patientIds);
    }
  }

  return normalized;
};

export const resendFax = (data: ResendFaxParams): Promise<FaxViewDTO> => (
  sendRequest<FaxViewDTO>(
    { url: `/api/eFax/resendFax?outboundFaxId=${data.outboundFaxId}` },
    { method: 'POST' },
  )
);

export const getContacts = (): Promise<FaxContactDTO[]> => (
  sendRequest<FaxContactDTO[]>({
    url: '/api/eFax/getFaxContacts',
  }, {
    method: 'GET',
  })
);

export const addContact = (data: Partial<FaxContactDTO>): Promise<FaxContactDTO> => (
  sendRequest<FaxContactDTO>({
    url: '/api/eFax/addFaxContact',
  }, {
    method: 'POST',
    data,
  })
);
